import React from 'react';
import { InvoiceRecord } from '@types';
import { UserRole } from '@config/workflow-config';

interface AssignmentSectionProps {
  invoice: InvoiceRecord;
  wasUserPreviouslyAssigned: boolean;
  userRole?: UserRole;
  onAssignToMe: () => void;
  onUnassign: () => void;
  isAssigning: boolean;
  isUnassigning: boolean;
}

export const AssignmentSection: React.FC<AssignmentSectionProps> = ({
  invoice,
  wasUserPreviouslyAssigned,
  userRole,
  onAssignToMe,
  onUnassign,
  isAssigning,
  isUnassigning
}) => {
  // Admin and Finance users can bypass separation of duties restrictions
  const canBypassSeparationOfDuties = userRole === UserRole.ADMINISTRATOR || userRole === UserRole.FINANCE;
  const shouldBlockAssignment = wasUserPreviouslyAssigned && !canBypassSeparationOfDuties;
  return (
    <div className="mb-6">
      {invoice.assigned_to_user ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">
                Assigned to: {invoice.assigned_to_user.name || invoice.assigned_to_user.email}
              </p>
              {invoice.assigned_to_user.name && (
                <p className="text-xs text-green-600">{invoice.assigned_to_user.email}</p>
              )}
            </div>
            <button
              onClick={onUnassign}
              disabled={isUnassigning}
              className="text-xs bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded transition-colors"
            >
              {isUnassigning ? 'Unassigning...' : 'Unassign'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="mb-3">
            <h3 className="text-sm font-medium text-blue-800 mb-1">Assignment Required</h3>
            <p className="text-sm text-blue-600">
              {shouldBlockAssignment 
                ? 'You were previously assigned to this invoice and cannot assign yourself again. Please contact an administrator.' 
                : 'You must assign this invoice to yourself before you can edit the form fields below.'}
            </p>
          </div>
          <button
            onClick={onAssignToMe}
            disabled={isAssigning || shouldBlockAssignment}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isAssigning ? 'Assigning...' : (shouldBlockAssignment ? 'Previously Assigned' : 'Assign to Me')}
          </button>
        </div>
      )}
    </div>
  );
};
